import { observer } from 'mobx-react-lite'
import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import { styled } from '../../../lib/stitches'
import { useTreeState } from '../../../stores/Root'
import { Column, Row } from '../../core/Components'
import { ChevronDown } from '../../core/icons'
import { PreviewPlayer } from '../../platform/explore/common/MediaPreview'
import Gradient from '../../platform/Gradient'
import { OpenDetailPageButton } from './Common'
import MediaTitleSection from './MediaTitleSection'
import PlayerControlsSection from './PlayerControlsSection'

const GradientContainer = styled(Gradient, {
  width: '100%',
  height: '100%',
  padding: '16px 16px',
  flex: '1',
  flexDirection: 'column',
  alignItems: 'space-between',
  position: 'absolute'
})

const MobilePlayerPageContainer = styled(Column, {
  position: 'fixed',
  top: '0',
  width: '100vw',
  height: '100%',
  zIndex: '200',
  justifyContent: 'space-between',
  padding: '16px 16px',
  flex: '1',
  alignItems: 'stretch',
  overflow: 'hidden',
  backgroundColor: '$gray04'
})

interface MainContentProps {
  media: IArpMedia
}

const MainContent: FC<MainContentProps> = observer(({ media }) => {
  const { globalPlayer } = useTreeState()

  if (!media) return null

  const mediaType = media.type
  return (
    <Column horizontalCenter css={{ width: '100%', maxWidth: '450px', margin: 'auto' }}>
      <PreviewPlayer index={media.id} type={mediaType} autoPlay={false} />
      <Row css={{ paddingTop: '16px', width: '100%' }}>
        <MediaTitleSection hideAvatar />
      </Row>
    </Column>
  )
})

interface TopControlsProps {
  media: IArpMedia
  close: () => void
}

const TopControls: FC<TopControlsProps> = ({ media, close }) => {
  const navigate = useNavigate()
  return (
    <Row css={{ justifyContent: 'space-between', marginBottom: '45px' }}>
      <Row onClick={close}>
        <ChevronDown height="24px" width="24px" />
      </Row>
      <OpenDetailPageButton
        onClick={() => {
          close()
          navigate(`/${media.type}?token=${media.id}`)
        }}
        disabled={!media}
      />
    </Row>
  )
}

interface MobilePlayerPageProps {
  close: () => void
}

const MobilePlayerPage: FC<MobilePlayerPageProps> = ({ close }) => {
  const { globalPlayer } = useTreeState()
  const [media, setMedia] = useState<IArpMedia>(null)

  useEffect(() => {
    ArpRegistry.getByIdAsync(globalPlayer.arpMediaIndex).then((arp) => setMedia(arp))
  }, [globalPlayer.arpMediaIndex])

  useEffect(() => {
    const html = document.querySelector('html')
    const currentOverflow = html.style.overflow
    html.style.overflow = 'hidden'

    return () => {
      html.style.overflow = currentOverflow
    }
  }, [])

  return (
    <MobilePlayerPageContainer animateAppear>
      <TopControls close={close} media={media} />
      <MainContent media={media} />
      <Row verticalCenter horizontalCenter>
        <PlayerControlsSection isMobile={false} />
      </Row>
    </MobilePlayerPageContainer>
  )
}

export default observer(MobilePlayerPage)
